import {
  StatHandler,
  UserInfo,
  StatEvent,
  StatContext,
  StatOptions,
  StatBatch,
  IdentifyParams,
  TrackParams,
  BatchParams,
  UserIds,
  WorkerData,
  StatFlags,
  PageParams,
} from './StatTypes'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const StatsApi = require('./stats-api')

let NEXT_STAT_HANDLER_ID = 1

// NOTE: special ID that will not be associated with the user,
// this is for cases when we want fully anonymous data
let FULLY_ANONYMOUS_ID: string | undefined
function getFullyAnonymousId(): string {
  if (!FULLY_ANONYMOUS_ID) {
    FULLY_ANONYMOUS_ID = createAnonymousId()
  }
  return FULLY_ANONYMOUS_ID
}

function createAnonymousId(): string {
  return StatsApi.UUID()
}

export function identify(statHandler: StatHandler, apiKey: string, user: UserInfo): void {
  if (!user.id) {
    // NOTE: this is to avoid accidental calls for guests who has no id
    statHandler.app.sendError('Stats invalid identify', 'Empty user id')
    return
  }

  let anonymousId = statHandler.anonymousId
  let userId = statHandler.userId

  if (userId !== user.id) {
    // NOTE: we reset anonymousId only if user was previously set, otherwise just update the userId
    if (userId) {
      anonymousId = createAnonymousId()
    }

    userId = user.id

    statHandler.app.storeUserIds({
      userId: userId,
      anonymousId: anonymousId,
    })

    updateUserForHandler(statHandler, anonymousId, userId, 'identify')
  }

  const params: IdentifyParams = {
    apiKey: apiKey,
    apiHost: statHandler.apiHost,
    context: getStatContext(statHandler),
    timestamp: new Date(),
    anonymousId: anonymousId,
    userId: userId,
    user: {
      id: user.id,
      name: user.name,
      email: user.email,
    },
  }
  sendEvent(statHandler, StatFlags.None, {
    event: 'identify',
    payload: params,
  })
}

function buildTrackParams(
  statHandler: StatHandler,
  apiKey: string,
  event: string,
  inputProperties: TrackParams['properties'] | undefined,
  flags: StatFlags,
  timestamp: Date,
): TrackParams | undefined {
  let result: TrackParams | undefined = undefined

  const onlyFirstSession = flags & StatFlags.OnlyFirstSession
  if (!onlyFirstSession || statHandler.isFirstSession) {
    let canBeSend = true

    const onceInSession = flags & StatFlags.OnceInSession
    if (onceInSession) {
      canBeSend = !statHandler.onceInSessionSentEvents.has(event)
      if (canBeSend) {
        statHandler.onceInSessionSentEvents.add(event)
      }
    }
    if (canBeSend) {
      let properties = inputProperties || {}
      if (!(flags & StatFlags.NoCommonParams)) {
        properties = statHandler.app.addCommonStatParams(properties)
      }
      const asAnonymous = !!(flags & StatFlags.Anonymous)
      const userIds = getUserIdsPayload(statHandler, asAnonymous)
      result = {
        apiKey: apiKey,
        apiHost: statHandler.apiHost,
        context: getStatContext(statHandler),
        timestamp: timestamp,
        event: event,
        properties: properties,
        anonymousId: userIds.anonymousId,
        userId: userIds.userId,
      }
    }
  }

  return result
}

function buildBatchFlags(flags: StatFlags): StatFlags {
  let batchFlags = StatFlags.None
  if (flags & StatFlags.OnlyForWorker) {
    batchFlags |= StatFlags.OnlyForWorker
  }
  return batchFlags
}

export function page(
  statHandler: StatHandler,
  apiKey: string,
  properties?: PageParams['properties'],
  flags = StatFlags.None,
): void {
  const asAnonymous = !!(flags & StatFlags.Anonymous)
  const userIds = getUserIdsPayload(statHandler, asAnonymous)

  const context = getStatContext(statHandler)

  // Title and path can be overriden from properties
  const {path = '', title = ''} = properties || {}

  if (path) {
    context.page.path = path
  }

  if (title) {
    context.page.title = title
  }

  const params: PageParams = {
    apiKey: apiKey,
    apiHost: statHandler.apiHost,
    context: getStatContext(statHandler),
    timestamp: new Date(),
    anonymousId: userIds.anonymousId,
    userId: userIds.userId,
  }

  sendEvent(statHandler, StatFlags.None, {
    event: 'page',
    payload: params,
  })
}

export function track(
  statHandler: StatHandler,
  apiKey: string,
  event: string,
  inputProperties?: TrackParams['properties'],
  flags = StatFlags.None,
  timestamp = new Date(),
): void {
  const trackParams = buildTrackParams(
    statHandler,
    apiKey,
    event,
    inputProperties,
    flags,
    timestamp,
  )
  if (trackParams) {
    if (!statHandler.batches || flags & StatFlags.SendImmediately) {
      sendEvent(statHandler, flags, {
        event: 'track',
        payload: trackParams,
      })
    } else {
      let apiKeyBucket = statHandler.batches.buckets.get(apiKey)
      if (!apiKeyBucket) {
        apiKeyBucket = new Map<number, StatBatch>()
        statHandler.batches.buckets.set(apiKey, apiKeyBucket)
      }

      const batchFlags = buildBatchFlags(flags)
      let batch = apiKeyBucket.get(batchFlags)
      if (!batch) {
        batch = {
          tracks: [],
          flags: batchFlags,
          flushAt: statHandler.batches.flushAt,
        }
        apiKeyBucket.set(batchFlags, batch)
      }

      if (batch.tracks.length >= batch.flushAt) {
        flushBatchInternal(statHandler, batch, apiKey)
      }
      batch.tracks.push(trackParams)
    }
  }
}

export function trackBatch(
  statHandler: StatHandler,
  apiKey: string,
  events: StatEvent[],
  flags = StatFlags.None,
): void {
  const tracks: TrackParams[] = []
  events.forEach(event => {
    const params = buildTrackParams(
      statHandler,
      apiKey,
      event.event,
      event.properties,
      flags,
      event.timestamp || new Date(),
    )
    if (params) {
      tracks.push(params)
    }
  })

  const tempBatch: StatBatch = {
    tracks: tracks,
    flags: buildBatchFlags(flags),
    flushAt: tracks.length,
  }
  flushBatchInternal(statHandler, tempBatch, apiKey)
}

export function clearBatches(statHandler: StatHandler): void {
  if (statHandler.batches) {
    statHandler.batches.buckets.clear()
  }
}

export function flushBatches(statHandler: StatHandler): void {
  if (statHandler.batches) {
    statHandler.batches.buckets.forEach((bucket, apiKey) => {
      bucket.forEach(batch => {
        flushBatchInternal(statHandler, batch, apiKey)
      })
    })
  }
}

export function flushBatch(statHandler: StatHandler, apiKey: string): void {
  if (statHandler.batches) {
    const bucket = statHandler.batches.buckets.get(apiKey)
    if (bucket) {
      bucket.forEach(batch => {
        flushBatchInternal(statHandler, batch, apiKey)
      })
    }
  }
}

function flushBatchInternal(statHandler: StatHandler, batch: StatBatch, apiKey: string) {
  if (batch.tracks.length) {
    const params: BatchParams = {
      apiKey: apiKey,
      apiHost: statHandler.apiHost,
      tracks: batch.tracks,
    }
    sendEvent(statHandler, batch.flags, {
      event: 'trackBatch',
      payload: params,
    })
    batch.tracks.length = 0
  }
}

function getUserIdsPayload(statHandler: StatHandler, asAnonymous: boolean): UserIds {
  const result = {
    anonymousId: asAnonymous ? getFullyAnonymousId() : statHandler.anonymousId,
    userId: asAnonymous ? undefined : statHandler.userId,
  }
  return result
}

function sendEvent(statHandler: StatHandler, flags: StatFlags, params: WorkerData): void {
  if (statHandler.worker) {
    statHandler.worker.postMessage(params)
  } else {
    const onlyWorker = !!(flags & StatFlags.OnlyForWorker)
    if (onlyWorker) {
      // noop, event should be sent only via worker
    } else {
      switch (params.event) {
        case 'identify':
          {
            StatsApi.identify(params.payload)
          }
          break

        case 'track':
          {
            StatsApi.track(params.payload)
          }
          break

        case 'trackBatch':
          {
            StatsApi.trackBatch(params.payload)
          }
          break

        case 'page':
          {
            StatsApi.page(params.payload)
          }
          break
      }
    }
  }
}

function getStatContext(statHandler: StatHandler): StatContext {
  const context: StatContext = {
    library: {
      name: statHandler.library,
      version: statHandler.version,
    },
    page: statHandler.app.getStatContextPage(statHandler),
    userAgent: statHandler.app.getUserAgent(),
  }
  return context
}

export function updateUserForHandler(
  statHandler: StatHandler,
  anonymousId: string | undefined,
  userId: string | undefined,
  source: string,
): void {
  if (!anonymousId || userId === 'null' || userId === 'undefined') {
    statHandler.app.sendError('Stats invalid userId', {
      anonymousId: anonymousId,
      userId: userId,
      source: source,
    })
  } else {
    statHandler.anonymousId = anonymousId
    statHandler.userId = userId
    statHandler.__lastUserSource = source
  }
}

export function createStatHandler(options: StatOptions): StatHandler {
  const statHandler: StatHandler = {
    __statId: NEXT_STAT_HANDLER_ID++,
    __lastUserSource: undefined,
    apiHost: options.apiHost,
    library: options.library,
    version: options.version,
    anonymousId: '',
    userId: undefined,

    app: options.app,

    // NOTE: just defaults, override them from outside
    isFirstSession: false,
    onceInSessionSentEvents: new Set<string>(),
    // NOTE: no worker by default, set this from outside if you need to
    worker: undefined,

    batches: undefined,
    flushBatchesIntervalId: undefined,
  }

  if (options.batches) {
    statHandler.batches = {
      buckets: new Map<string, Map<number, StatBatch>>(),
      flushAt: options.batches.flushAt,
    }
  }

  const userData = statHandler.app.getStoredUserIds()
  if (!userData.anonymousId) {
    userData.anonymousId = createAnonymousId()
    statHandler.app.storeUserIds(userData as UserIds)
  }

  updateUserForHandler(statHandler, userData.anonymousId, userData.userId, 'initialization')

  return statHandler
}

export function destroyStatHandler(statHandler: StatHandler): void {
  // noop for now?
}
